import { useState } from 'react'
import { toast } from 'sonner'
import { AudioLines, Film, Loader2, Mic, Monitor, X } from 'lucide-react'
import { Input } from '@orisini/ui/components/input'
import { cn } from '@orisini/ui/lib/utils'
import { AGENT_THRESHOLD_SEC, DURATION_LABEL, formatDuration, uploadAsset, type Asset, type DurationMode, type DurationSpec } from '../lib/content'

export interface AudioPick {
  asset: Asset
  role: 'backsound' | 'sfx'
  label: string
}

export interface VideoMaterialValue {
  duration: DurationSpec
  voiceover: Asset | null
  audio: AudioPick[]
  broll: Asset[]
}

export const EMPTY_VIDEO: VideoMaterialValue = { duration: { mode: 'auto' }, voiceover: null, audio: [], broll: [] }

/** Batas platform (sama dengan PLATFORM_MAX_SEC di studio-kit, dicek 2026-11). */
const PLATFORM_MAX: Record<string, number> = { Instagram: 20 * 60, Pinterest: 15 * 60 }

/** Perkiraan detik video menurut pilihan — untuk peringatan, bukan janji. */
export function expectedSec(v: VideoMaterialValue): number | null {
  if (v.voiceover?.durationMs) return v.voiceover.durationMs / 1000
  if (v.duration.mode === '1-5') return 300
  if (v.duration.mode === '5-10') return 600
  if (v.duration.mode === 'custom') return (v.duration.minutes ?? 0) * 60
  return null
}

function useUpload() {
  const [busy, setBusy] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const run = async (slot: string, file: File | undefined, done: (a: Asset) => void) => {
    if (!file) return
    setBusy(slot)
    setProgress(0)
    try {
      done(await uploadAsset(file, setProgress))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't upload the file")
    } finally {
      setBusy(null)
    }
  }
  return { busy, progress, run }
}

function UploadButton({ label, accept, busy, progress, icon: Icon, onFile }: { label: string; accept: string; busy: boolean; progress: number; icon: typeof Mic; onFile: (f: File | undefined) => void }) {
  return (
    <label className={cn('inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:border-foreground/40', busy && 'pointer-events-none opacity-70')}>
      {busy ? <Loader2 className="size-4 animate-spin" /> : <Icon className="size-4" />}
      {busy ? `Uploading… ${Math.round(progress * 100)}%` : label}
      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          onFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />
    </label>
  )
}

function AssetChip({ asset, onRemove }: { asset: Asset; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-sm">
      <span className="max-w-[14rem] truncate">{asset.filename ?? 'File'}</span>
      {asset.durationMs ? <span className="tabular-nums text-muted-foreground">{formatDuration(asset.durationMs / 1000)}</span> : null}
      <button type="button" onClick={onRemove} aria-label={`Remove ${asset.filename ?? 'file'}`}>
        <X className="size-3.5" />
      </button>
    </span>
  )
}

const AUDIO_ACCEPT = 'audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,.mp3,.wav,.m4a'

/**
 * Materi video di Create: durasi, voice-over (narasi; durasi video ikut
 * audio ini), Assets audio (backsound/SFX), dan klip b-roll.
 */
export function VideoMaterial({ value, onChange, platforms }: { value: VideoMaterialValue; onChange: (v: VideoMaterialValue) => void; platforms: string[] }) {
  const up = useUpload()
  const sec = expectedSec(value)
  const lewatPlatform = platforms.filter((p) => sec !== null && PLATFORM_MAX[p] && sec > PLATFORM_MAX[p]!)
  const setDur = (mode: DurationMode) => onChange({ ...value, duration: mode === 'custom' ? { mode, minutes: value.duration.minutes ?? 3 } : { mode } })

  return (
    <div className="space-y-5 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold">Video</h3>

      <div className="space-y-1.5">
        <span className="text-sm font-medium">Length</span>
        {value.voiceover ? (
          <p className="text-sm text-muted-foreground">Follows your voice-over — {formatDuration((value.voiceover.durationMs ?? 0) / 1000)}.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {(Object.keys(DURATION_LABEL) as DurationMode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setDur(m)}
                className={cn('rounded-md border px-3 py-1.5 text-sm', value.duration.mode === m ? 'border-primary bg-primary/10' : 'border-border')}
              >
                {DURATION_LABEL[m]}
              </button>
            ))}
            {value.duration.mode === 'custom' && (
              <span className="flex items-center gap-1.5 text-sm">
                <Input
                  type="number"
                  min={0.25}
                  max={60}
                  step={0.25}
                  className="h-9 w-24"
                  value={value.duration.minutes ?? ''}
                  onChange={(e) => onChange({ ...value, duration: { mode: 'custom', minutes: e.target.value === '' ? null : Number(e.target.value) } })}
                  aria-label="Minutes"
                />
                minutes
              </span>
            )}
          </div>
        )}
        {value.duration.mode === 'auto' && !value.voiceover && <p className="text-xs text-muted-foreground">Orisini picks a length from how much the source says.</p>}
        {sec !== null && sec > AGENT_THRESHOLD_SEC && (
          <p className="flex items-start gap-1.5 rounded-md bg-amber-500/10 p-2 text-xs text-amber-300">
            <Monitor className="mt-0.5 size-3.5 shrink-0" />
            Videos longer than 10 minutes are rendered on your computer by Orisini Agent, so keep it open. They don't use your video minutes.
          </p>
        )}
        {lewatPlatform.length > 0 && (
          <p className="text-xs text-amber-300">
            {lewatPlatform.join(' and ')} {lewatPlatform.length === 1 ? 'takes' : 'take'} videos up to {Math.round(PLATFORM_MAX[lewatPlatform[0]!]! / 60)} minutes. A longer video is still made, but stays in the Content Bank.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <span className="text-sm font-medium">Voice-over (optional)</span>
        <div className="flex flex-wrap items-center gap-2">
          {value.voiceover ? (
            <AssetChip asset={value.voiceover} onRemove={() => onChange({ ...value, voiceover: null })} />
          ) : (
            <UploadButton label="Upload voice-over" accept={AUDIO_ACCEPT} icon={Mic} busy={up.busy === 'vo'} progress={up.progress} onFile={(f) => void up.run('vo', f, (a) => onChange({ ...value, voiceover: a }))} />
          )}
        </div>
        <p className="text-xs text-muted-foreground">Your own narration. Orisini transcribes it to time the scenes and write the on-screen text. Without one, a voice is generated from the script.</p>
      </div>

      <div className="space-y-1.5">
        <span className="text-sm font-medium">Assets (optional)</span>
        <div className="space-y-2">
          {value.audio.map((a, i) => (
            <div key={a.asset.id} className="flex flex-wrap items-center gap-2">
              <AssetChip asset={a.asset} onRemove={() => onChange({ ...value, audio: value.audio.filter((_, k) => k !== i) })} />
              <select
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                value={a.role}
                onChange={(e) => onChange({ ...value, audio: value.audio.map((x, k) => (k === i ? { ...x, role: e.target.value as AudioPick['role'] } : x)) })}
                aria-label="Use as"
              >
                <option value="backsound">Background music</option>
                <option value="sfx">Sound effect</option>
              </select>
              {a.role === 'sfx' && (
                <Input
                  className="h-8 w-44"
                  placeholder="whoosh, ding, intro…"
                  value={a.label}
                  maxLength={40}
                  onChange={(e) => onChange({ ...value, audio: value.audio.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)) })}
                  aria-label="Sound effect label"
                />
              )}
            </div>
          ))}
          <UploadButton
            label="Add music or a sound effect"
            accept={AUDIO_ACCEPT}
            icon={AudioLines}
            busy={up.busy === 'audio'}
            progress={up.progress}
            onFile={(f) =>
              void up.run('audio', f, (a) =>
                onChange({
                  ...value,
                  audio: [...value.audio, { asset: a, role: value.audio.some((x) => x.role === 'backsound') || (a.durationMs ?? 0) < 8000 ? 'sfx' : 'backsound', label: (a.filename ?? '').replace(/\.[^.]+$/, '').slice(0, 40) }],
                }),
              )
            }
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Music loops under the voice and gets quieter while someone speaks. Sound effects go where their label says: "whoosh" on scene changes, "intro" at the start, anything else on key moments. Without assets, the project's audio library is used. Only upload audio you have the right to use.
        </p>
      </div>

      <div className="space-y-1.5">
        <span className="text-sm font-medium">Video clips (optional)</span>
        <div className="flex flex-wrap items-center gap-2">
          {value.broll.map((b, i) => (
            <AssetChip key={b.id} asset={b} onRemove={() => onChange({ ...value, broll: value.broll.filter((_, k) => k !== i) })} />
          ))}
          <UploadButton label="Add a clip" accept="video/mp4,video/quicktime,.mp4,.mov" icon={Film} busy={up.busy === 'broll'} progress={up.progress} onFile={(f) => void up.run('broll', f, (a) => onChange({ ...value, broll: [...value.broll, a] }))} />
        </div>
        <p className="text-xs text-muted-foreground">Shown behind the text, one clip per scene after the first. MP4 or MOV, up to 500 MB.</p>
      </div>
    </div>
  )
}
